import React, { useState } from 'react';
import { X, Minus, Plus } from 'lucide-react';
import {
    getCleanVariantLabel,
    hasMeaningfulVariantOptions,
    sanitizeProductDisplayName,
} from '../../utils/productLabels';

const formatCurrency = (amount) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(amount);

export default function ProductDetailModal({ product, onClose, onAdd, theme, storeType, config, primaryColor, allowCheckoutWithoutStock = false, showPrices = true }) {
    const [qty, setQty] = useState(1);
    const [note, setNote] = useState('');
    const [selectedVariant, setSelectedVariant] = useState(
        product.variants?.find((variant) => getCleanVariantLabel(variant?.name)) || product.variants?.[0] || null,
    );
    const currentPrice = selectedVariant ? (product.price + selectedVariant.priceMod) : product.price;
    const currentStock = selectedVariant ? selectedVariant.stock : product.stock;
    const btnColor = primaryColor || theme?.primaryColor || theme?.modalColors?.text || '#0F172A';
    const showVariantOptions = hasMeaningfulVariantOptions(product.variants);
    const productName = sanitizeProductDisplayName(product.name || 'Producto');

    return (
        <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md bg-[var(--surface)] rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300 flex flex-col max-h-[85vh]" style={{backgroundColor: theme?.modalColors?.bg || '#fff', color: theme?.modalColors?.text}}>
                <div className="h-64 bg-[var(--surface-2)] flex items-center justify-center relative shrink-0">
                    {product.image_url ? (
                        <img src={product.image_url} className="w-full h-full object-cover" alt={productName}/>
                    ) : (
                        <span className="text-8xl select-none opacity-50">{storeType === 'gastronomy' ? '🍔' : '📦'}</span>
                    )}
                    <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-[var(--surface)]/50 hover:bg-[var(--surface)] text-black backdrop-blur-md transition-colors shadow-sm"><X size={20}/></button>
                </div>
                <div className="p-6 overflow-y-auto">
                    {/* NUEVO: Mostrar Marca */}
                    {product.brand && (
                        <span className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-1 block w-fit px-2 py-0.5 rounded" style={{ backgroundColor: theme?.modalColors?.border || '#e5e7eb', color: btnColor }}>{product.brand}</span>
                    )}
                    <h2 className="text-2xl font-black leading-tight mb-2">{productName}</h2>
                    {config?.show_stock && (
                        <div className="text-xs font-bold mb-2" style={{ color: btnColor }}>Stock disponible: {currentStock}</div>
                    )}
                    <p className="opacity-70 text-sm mb-6 leading-relaxed whitespace-pre-wrap">{product.description || "Sin descripción."}</p>
                    
                    {showVariantOptions && (
                        <div className="mb-6">
                            <label className="text-[10px] font-bold uppercase opacity-50 block mb-2">Opciones</label>
                            <div className="flex flex-wrap gap-2">
                                {product.variants.filter(v => getCleanVariantLabel(v.name)).map(v => (
                                    <button key={v.id} onClick={() => setSelectedVariant(v)} className={`px-3 py-2 rounded-lg text-xs font-bold border transition-all ${selectedVariant?.id === v.id ? 'ring-2 ring-offset-1 opacity-100' : 'opacity-60'}`} style={{borderColor: 'currentColor'}}>
                                        {getCleanVariantLabel(v.name)} {showPrices && v.priceMod > 0 && `(+${formatCurrency(v.priceMod)})`}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <div className="mb-6">
                        <label className="text-[10px] font-bold uppercase opacity-50 block mb-2">Nota para el pedido</label>
                        <textarea className="w-full border rounded-xl p-3 text-sm focus:ring-2 outline-none resize-none bg-transparent" placeholder="Ej: Sin cebolla, envolver para regalo..." rows={2} value={note} onChange={e => setNote(e.target.value)} />
                    </div>

                    <div className="flex items-center gap-4 pt-4 pb-20 md:pb-4 border-t" style={{borderColor: theme?.modalColors?.border}}>
                        <div className="flex items-center border rounded-xl h-12" style={{borderColor: theme?.modalColors?.border}}>
                            <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 h-full hover:opacity-70"><Minus size={16}/></button>
                            <span className="w-8 text-center font-bold">{qty}</span>
                            <button onClick={() => setQty(allowCheckoutWithoutStock ? qty + 1 : Math.min(qty + 1, currentStock))} className="px-3 h-full hover:opacity-70"><Plus size={16}/></button>
                        </div>
                        <button onClick={() => onAdd(product, qty, note, selectedVariant)} disabled={!allowCheckoutWithoutStock && currentStock <= 0} className="flex-1 h-12 text-white rounded-md font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-colors shadow-lg" style={{backgroundColor: btnColor}}>
                            {!allowCheckoutWithoutStock && currentStock <= 0 ? 'Sin stock' : showPrices ? `Agregar ${formatCurrency(currentPrice * qty)}` : 'Agregar al pedido'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
